import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen luxury-gradient">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-secondary max-w-2xl mx-auto leading-relaxed">
            Our commitment to excellence extends to every aspect of our service relationship. 
            These terms establish the foundation for delivering extraordinary audiovisual experiences 
            with precision and integrity.
          </p>
          <div className="mt-8 text-sm text-secondary/80">
            <p>Effective Date: January 1, 2026</p>
            <p>Last Updated: January 1, 2026</p>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-effect rounded-xl p-8 md:p-12 space-y-12">
            
            {/* Acceptance */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Agreement and Acceptance</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  By engaging with Parallax Cinema's services, scheduling consultations, or purchasing our 
                  products, you enter into a binding agreement governed by these Terms of Service. These terms 
                  reflect our commitment to delivering reference-quality experiences through precision engineering 
                  and uncompromising service standards.
                </p>
                <p>
                  Your acceptance of these terms signifies your agreement to conduct business with the same 
                  level of professionalism and integrity that defines our approach to audiovisual excellence.
                </p>
              </div>
            </div>

            {/* Services Overview */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Our Services</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-highlight mb-3">Exclusive Consultation Services</h3>
                  <ul className="text-secondary space-y-2 pl-4">
                    <li>• Private showroom demonstrations by appointment only</li>
                    <li>• Personalized home theater design and specification</li>
                    <li>• Professional acoustic assessment and room analysis</li>
                    <li>• Custom equipment selection and configuration</li>
                    <li>• Integration planning and technical consultation</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-highlight mb-3">Premium Product Sales</h3>
                  <ul className="text-secondary space-y-2 pl-4">
                    <li>• Parallax Pro 8K Projector ($35,000 - $50,000)</li>
                    <li>• Parallax Immersion 5.1 Surround System ($25,000 - $40,000)</li>
                    <li>• Parallax Studio Soundbar ($8,000 - $15,000)</li>
                    <li>• Parallax Vision Projection Screen ($6,000 - $12,000)</li>
                    <li>• Professional accessories and calibration equipment</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-highlight mb-3">White-Glove Installation</h3>
                  <ul className="text-secondary space-y-2 pl-4">
                    <li>• Professional installation by certified technicians</li>
                    <li>• Precision calibration and system optimization</li>
                    <li>• Custom mounting and acoustic treatment coordination</li>
                    <li>• Comprehensive system training and documentation</li>
                    <li>• Post-installation support and maintenance services</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Consultation Terms */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Consultation and Design Services</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <h3 className="text-lg font-medium text-highlight mb-3">Appointment Requirements</h3>
                <p>
                  All consultations are conducted by appointment only. Appointments must be scheduled at 
                  least 48 hours in advance and confirmed 24 hours prior. Missed appointments will be 
                  charged the full consultation fee with no rescheduling available.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Consultation Fees</h3>
                <ul className="space-y-2 pl-4">
                  <li>• Showroom consultation: $500 (non-refundable)</li>
                  <li>• Home assessment: $1,500 (non-refundable)</li>
                  <li>• Custom design services: $2,500 - $10,000 (non-refundable)</li>
                  <li>• Travel beyond 25-mile radius: $250 per hour plus expenses</li>
                  <li>• All consultation fees are final and non-refundable</li>
                  <li>• Consultation fees are separate from and do not apply toward product purchases</li>
                </ul>
                
                <h3 className="text-lg font-medium text-highlight mb-3">No Guarantees</h3>
                <p>
                  Consultation services are provided for information purposes only. We make no guarantees 
                  about system performance, compatibility, or suitability. All recommendations are opinions 
                  only and customers assume all risks associated with implementation.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Intellectual Property</h3>
                <p>
                  All custom designs, specifications, and recommendations remain the exclusive intellectual 
                  property of Parallax Cinema regardless of payment status. Unauthorized use, reproduction, 
                  or sharing of our designs is strictly prohibited and subject to legal action.
                </p>
              </div>
            </div>

            {/* Purchase Terms */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Purchase Terms and Conditions</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <h3 className="text-lg font-medium text-highlight mb-3">Pricing and Payment</h3>
                <ul className="space-y-2 pl-4">
                  <li>• All prices are subject to change without notice</li>
                  <li>• 50% non-refundable deposit required to initiate any order</li>
                  <li>• Balance due prior to delivery and installation</li>
                  <li>• Payment accepted via wire transfer or certified check only</li>
                  <li>• No financing options available - payment in full required</li>
                  <li>• All deposits and payments are final and non-refundable</li>
                </ul>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Order Processing</h3>
                <p>
                  Custom equipment orders typically require 8-24 weeks for production and delivery. 
                  Lead times are estimates only and may change without notice. We are not responsible 
                  for delays in production, shipping, or delivery. Rush orders are not available.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">No Cancellation Policy</h3>
                <p>
                  <strong>Orders cannot be cancelled under any circumstances once deposit is received.</strong> 
                  This policy applies regardless of production status, delivery delays, or changes in 
                  customer circumstances. All deposits and payments are immediately forfeited upon 
                  order confirmation.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Delivery and Installation</h3>
                <ul className="space-y-2 pl-4">
                  <li>• Delivery and installation scheduled at our convenience</li>
                  <li>• Customer must provide all electrical and structural requirements</li>
                  <li>• Additional charges will apply for any installation complications</li>
                  <li>• Delivery dates are estimates only and not guaranteed</li>
                  <li>• Customer responsible for all permits and approvals</li>
                </ul>
              </div>
            </div>

            {/* Installation Terms */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Installation Services</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <h3 className="text-lg font-medium text-highlight mb-3">Installation Service Disclaimer</h3>
                <p>
                  Installation services are provided as a convenience only. Customer assumes all risks 
                  associated with installation including but not limited to property damage, personal injury, 
                  equipment damage, and system performance. We make no guarantees about installation quality, 
                  safety, or compliance with local codes.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Customer Responsibilities</h3>
                <ul className="space-y-2 pl-4">
                  <li>• Provide adequate electrical supply and appropriate circuits (customer expense)</li>
                  <li>• Ensure structural integrity for mounting equipment (customer verification required)</li>
                  <li>• Obtain all necessary permits and approvals (customer responsibility)</li>
                  <li>• Provide climate-controlled environment during installation</li>
                  <li>• Clear access routes and workspace preparation</li>
                  <li>• Assume all liability for installation-related issues</li>
                </ul>
                
                <h3 className="text-lg font-medium text-highlight mb-3">No Installation Warranty</h3>
                <p>
                  We provide no warranty on installation workmanship, mounting hardware, or system integration. 
                  If equipment falls, fails, or causes damage after installation, customer assumes full 
                  responsibility and liability. Installation is performed "as is" with no guarantees of 
                  any kind.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Additional Charges</h3>
                <p>
                  Any complications, additional work, or unforeseen circumstances discovered during installation 
                  will result in additional charges at our discretion. Customer is responsible for all 
                  additional costs regardless of original quote. Work may be stopped if additional payment 
                  is not immediately provided.
                </p>
              </div>
            </div>

            {/* Product Information */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Product Information and Disclaimers</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <h3 className="text-lg font-medium text-highlight mb-3">Manufacturer Warranties Only</h3>
                <p>
                  Products may include manufacturer warranties that are passed through to the customer. 
                  Parallax Cinema makes no representations about warranty coverage, service availability, 
                  or manufacturer support. All warranty claims must be handled directly with manufacturers.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">No Performance Guarantees</h3>
                <p>
                  While our products represent advanced audiovisual technology, we make no guarantees 
                  regarding performance, compatibility, or suitability for any particular purpose. 
                  Product specifications are estimates and may vary based on installation environment 
                  and usage conditions.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Technology Changes</h3>
                <p>
                  Technology evolves rapidly. We are not responsible for products becoming obsolete, 
                  incompatible with future standards, or superseded by newer technology. No trade-ins, 
                  upgrades, or technology migration services are provided.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Installation Disclaimers</h3>
                <p>
                  Installation services are provided as a convenience only. We are not responsible for 
                  any damage to property, existing systems, or personal injury during installation. 
                  Customers must ensure adequate electrical supply, structural support, and environmental 
                  conditions. Additional charges may apply for any complications discovered during installation.
                </p>
              </div>
            </div>

            {/* Sales Final Policy */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Final Sale Policy</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <h3 className="text-lg font-medium text-highlight mb-3">All Sales Are Final</h3>
                <p>
                  Due to the bespoke nature of our ultra-luxury audiovisual systems and the precision engineering 
                  required for each installation, <strong>all sales are final</strong>. Once an order is confirmed 
                  and deposit received, no cancellations, returns, or refunds will be accepted under any circumstances.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">No Return Policy</h3>
                <p>
                  Parallax Cinema does not accept returns of any kind. This includes, but is not limited to:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• Change of mind or buyer's remorse</li>
                  <li>• Compatibility issues with existing equipment</li>
                  <li>• Installation challenges or spatial constraints</li>
                  <li>• Performance expectations not met</li>
                  <li>• Financial circumstances or funding changes</li>
                  <li>• Defective or damaged products (see warranty terms)</li>
                </ul>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Order Confirmation Requirements</h3>
                <p>
                  By placing an order, you acknowledge that you have:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• Thoroughly reviewed all specifications and requirements</li>
                  <li>• Confirmed compatibility with your installation environment</li>
                  <li>• Verified all measurements and technical specifications</li>
                  <li>• Secured necessary approvals and permits for installation</li>
                  <li>• Understood the final sale nature of this transaction</li>
                </ul>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Pre-Purchase Consultation</h3>
                <p>
                  Given the final nature of all sales, we strongly encourage comprehensive consultation 
                  before purchase. Our showroom demonstrations and home assessments are designed to 
                  ensure complete satisfaction before commitment. All questions and concerns must be 
                  addressed prior to order confirmation.
                </p>
              </div>
            </div>

            {/* Liability Disclaimer */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Limitation of Liability and Disclaimers</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <h3 className="text-lg font-medium text-highlight mb-3">Complete Liability Disclaimer</h3>
                <p>
                  <strong>PARALLAX CINEMA SHALL NOT BE LIABLE FOR ANY DAMAGES WHATSOEVER</strong>, including 
                  but not limited to direct, indirect, incidental, consequential, punitive, or special damages 
                  arising from or related to:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• Purchase, installation, or use of any products or services</li>
                  <li>• Equipment failure, malfunction, or performance issues</li>
                  <li>• Property damage during delivery, installation, or operation</li>
                  <li>• Personal injury or death related to equipment or installation</li>
                  <li>• Loss of use, revenue, profits, or business opportunities</li>
                  <li>• Data loss or corruption</li>
                  <li>• Compatibility issues with existing systems</li>
                  <li>• Changes in technology or obsolescence</li>
                </ul>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Use at Your Own Risk</h3>
                <p>
                  All products and services are provided "AS IS" without any warranties, express or implied. 
                  You assume all risks associated with the purchase, installation, and operation of audiovisual 
                  equipment. This includes but is not limited to risks of property damage, personal injury, 
                  fire, electrical hazards, and equipment malfunction.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">No Warranties</h3>
                <p>
                  While manufacturers may provide limited warranties on specific components, Parallax Cinema 
                  makes no warranties of any kind regarding performance, merchantability, fitness for purpose, 
                  or any other matter. Any manufacturer warranties are passed through to the customer without 
                  any guarantee or representation by Parallax Cinema.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Indemnification</h3>
                <p>
                  You agree to indemnify and hold harmless Parallax Cinema, its employees, agents, and 
                  representatives from any claims, damages, losses, or expenses (including attorney's fees) 
                  arising from your use of our products or services, or any violation of these terms.
                </p>
                
                <h3 className="text-lg font-medium text-highlight mb-3">Third Party Services</h3>
                <p>
                  Any third-party installation, delivery, or service providers act as independent contractors. 
                  Parallax Cinema is not responsible for their actions, performance, or any damages they may cause. 
                  All arrangements with third parties are solely between you and those parties.
                </p>
              </div>
            </div>

            {/* Terms Updates */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Terms Modifications</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  We reserve the right to update these terms at any time without notice. Material changes 
                  may be communicated to existing customers, but continued use of our services constitutes 
                  acceptance of any modifications. It is your responsibility to review these terms periodically.
                </p>
                <p>
                  For active projects, we reserve the right to apply updated terms to existing contracts 
                  at our discretion. No prior version of these terms shall be binding if superseded by 
                  updated terms.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="border-t border-secondary/20 pt-8">
              <h2 className="text-2xl font-semibold text-accent mb-6">Contact Information</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  For questions about these Terms of Service or to discuss your audiovisual requirements:
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="text-accent font-medium mb-2">Customer Service</h4>
                    <p>Email: consultation@parallaxcinema.com</p>
                    <p>Phone: +1 (555) 123-4567</p>
                    <p>Monday - Friday: 10AM - 6PM</p>
                    <p>Saturday: 10AM - 4PM</p>
                    <p>Sunday: By appointment</p>
                  </div>
                  <div>
                    <h4 className="text-accent font-medium mb-2">Showroom</h4>
                    <p>205 Diamond Boulevard, Unit 339</p>
                    <p>Anchorage, AK 99575</p>
                    <p><Link to="/contact" className="text-accent hover:text-highlight">Schedule a Private Consultation</Link></p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService; 